import React, { useEffect, useId, useRef, useState } from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";

import { Action, Category, Label } from "@/constants/events";
import { useATBStats } from "@/hooks/useATBStats";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getCartSessionId } from "@/redux/selectors/cart";
import { getUserAgent } from "@/redux/selectors/userAgent";
import {
  disableLoadingSpinner,
  enableLoadingSpinner,
} from "@/redux/slices/header";

import Button from "@/components/botanic/Button";
import { ButtonProps } from "@/components/botanic/Button/Button";

const AddToBagModal = dynamic(
  () => import("@/components/AddToBagModal").then((mod) => mod.AddToBagModal),
  { ssr: false },
);

type AddToBagButtonProps = {
  addingToBagText?: string;
  className?: string;
  ctaText?: string;
  defaultVariantId: number;
  iconDimensions?: string;
  immediatelyAddToBag?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReservation?: () => void;
  quantity?: number;
  successText?: string;
  variant?: ButtonProps["variant"];
  eventCategory?: Category;
  showContinueShopping?: boolean;
  showIcon?: boolean;
  inStoreCart?: boolean;
  trackingInfo: {
    menuItemId?: number;
    dispensaryId?: number;
    brandId?: number;
    productId?: number;
    strainId?: number;
    merchandisingCampaignId?: number;
    personalizationUserWeightIds?: string[];
  };
};

const AddToBagButton: React.FC<AddToBagButtonProps> = ({
  addingToBagText = "adding",
  className,
  ctaText = "add to cart",
  defaultVariantId,
  iconDimensions,
  immediatelyAddToBag = false,
  onClick = () => {},
  onReservation = () => {},
  quantity,
  successText = "added!",
  variant,
  eventCategory = Category.addToBag,
  showContinueShopping = false,
  showIcon = true,
  inStoreCart = false,
  trackingInfo: {
    menuItemId,
    dispensaryId,
    brandId,
    merchandisingCampaignId,
    productId,
    strainId,
    personalizationUserWeightIds,
  },
}) => {
  const pathname = usePathname();
  const [viewingModal, setViewingModal] = useState(false);
  const [isAddingToBag, setIsAddingToBag] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { isMobile, isIOS } = useSelector(getUserAgent);

  const spinnerId = useId();

  const dispatch = useDispatch();
  const cartSessionId = useSelector(getCartSessionId);

  const isBagPage = pathname === "/bag";

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { reportATBStats } = useATBStats();

  const { publishEvent } = useEventTracker();

  useEffect(() => {
    if (showSuccessMessage) {
      setIsAddingToBag(false);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 1200);
    }
  }, [showSuccessMessage]);

  const dispatchBagSpinner = (
    action: typeof enableLoadingSpinner | typeof disableLoadingSpinner,
  ) => {
    if (isBagPage) {
      dispatch(action(spinnerId));
    }
  };
  const addToBag = async (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatchBagSpinner(enableLoadingSpinner);
    setIsAddingToBag(true);
    onClick(e);
    setViewingModal(true);

    publishEvent({
      action: Action.click,
      brandId: brandId,
      cartId: cartSessionId,
      category: eventCategory,
      dispensaryId: dispensaryId,
      label: inStoreCart
        ? `add to bag - shop every menu ${
            isMobile ? (isIOS ? "ios" : "android") : "desktop"
          }`
        : `${Label.addToBag}${
            isMobile ? (isIOS ? " ios" : " android") : " desktop"
          }`,
      menuItemId: menuItemId,
      merchandisingCampaignId: merchandisingCampaignId,
      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      personalizationUserWeightIds,
      productId: productId,
      strainId: strainId,
    });
  };

  return (
    <>
      <Button
        data-testid="add-to-cart-button"
        iconFilePath={showIcon ? "cart_alt.svg" : undefined}
        disabled={showSuccessMessage}
        onClick={addToBag}
        iconDimensions={iconDimensions}
        className={classNames("w-full disabled", className)}
        ref={buttonRef}
        variant={variant}
        submittingText={addingToBagText}
        isSubmitting={isAddingToBag}
      >
        <div>{showSuccessMessage ? successText : ctaText}</div>
      </Button>

      {viewingModal && (
        <AddToBagModal
          immediatelyAddToBag={immediatelyAddToBag}
          inStoreCart={inStoreCart}
          onDismiss={() => {
            setViewingModal(false);
            setIsAddingToBag(false);
            dispatchBagSpinner(disableLoadingSpinner);
          }}
          onReservation={() => {
            reportATBStats({ atbAction: true, atbError: undefined });
            setShowSuccessMessage(true);
            onReservation();
          }}
          onReservationFail={(error: string) => {
            setIsAddingToBag(false);
            dispatchBagSpinner(disableLoadingSpinner);
            reportATBStats({ atbAction: true, atbError: error });
          }}
          quantity={quantity}
          returnFocusRef={buttonRef}
          variantId={defaultVariantId}
          eventCategory={eventCategory}
          showContinueShopping={showContinueShopping}
        />
      )}
    </>
  );
};

export default AddToBagButton;
